import React from 'react'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { useStableId } from './useStableId'
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SelectProps,
} from '@mui/material'
import { FormFieldShell } from './FormFieldShell'
import { resolveFieldSurface } from './FieldSurfaceContext'
import { getFieldSurfaceDefaults, shouldUseLabelAsPlaceholder } from './fieldSurfaces/registry'
import { isAboveLabelLayout, isOutlineLabelLayout, shouldShrinkOutlineLabel } from './formFieldTokens'
import {
  getFormFieldOutlineLabelSx,
  getFormFieldRootSx,
  KIOT_FORM_FIELD_CLASS,
  mergeSx,
} from './formFieldStyles'
import type { BaseFormFieldProps, SelectOption } from './formFieldTypes'

type SelectInputBaseProps<T extends string | number = string | number> = BaseFormFieldProps & {
  options: SelectOption<T>[]
  /** Hiển thị dòng trống đầu danh sách */
  allowEmpty?: boolean
  emptyLabel?: string
  displayEmpty?: boolean
  disablePortal?: boolean
  MenuProps?: SelectProps<T>['MenuProps']
  /** Thay icon mũi tên dropdown (vd. clear khi đã chọn). */
  IconComponent?: SelectProps<T>['IconComponent']
}

export type SelectInputProps<T extends string | number = string | number> = SelectInputBaseProps &
  (
    | {
        multiple?: false
        value?: T | ''
        onChange: (value: T) => void
        renderValue?: SelectProps<T>['renderValue']
      }
    | {
        multiple: true
        value?: T[]
        onChange: (value: T[]) => void
        renderValue?: (value: T[]) => React.ReactNode
      }
  )

export function SelectInput<T extends string | number = string | number>(props: SelectInputProps<T>) {
  const {
    label,
    labelAction,
    onChange,
    options,
    required,
    disabled,
    error,
    helperText,
    size,
    fullWidth,
    sx,
    id: idProp,
    allowEmpty = false,
    emptyLabel = '—',
    displayEmpty,
    renderValue,
    disablePortal,
    MenuProps,
    IconComponent,
    multiple,
    surface: surfaceProp,
  } = props

  const surface = resolveFieldSurface(surfaceProp)
  const outlineShrink = shouldShrinkOutlineLabel(surface)
  const labelAsPlaceholder = shouldUseLabelAsPlaceholder(surface)
  const surfaceDefaults = getFieldSurfaceDefaults(surface)
  const resolvedSize = size ?? surfaceDefaults.size
  const resolvedFullWidth = fullWidth ?? surfaceDefaults.fullWidth
  const shellLabel = labelAsPlaceholder ? undefined : label
  const resolvedEmptyLabel = labelAsPlaceholder && emptyLabel === '—' ? (label ?? emptyLabel) : emptyLabel
  const requested = props.value ?? (multiple ? [] : '')
  const matchedOption = multiple
    ? undefined
    : options.find((o) => o.value === requested || String(o.value) === String(requested))
  const value = multiple ? requested : matchedOption ? matchedOption.value : ''
  const autoId = useStableId()
  const id = idProp ?? autoId
  const labelId = `${id}-label`

  const handleChange = (event: SelectChangeEvent<T | T[]>) => {
    if (multiple) {
      const raw = event.target.value
      const next: T[] = Array.isArray(raw)
        ? [...raw]
        : typeof raw === 'string'
          ? raw === ''
            ? []
            : (raw.split(',') as T[])
          : []
      const changeMulti = onChange as (v: T[]) => void
      changeMulti(next)
      return
    }
    const changeSingle = onChange as (v: T) => void
    changeSingle(event.target.value as T)
  }

  const resolveOptionLabel = (v: T | ''): React.ReactNode => {
    if (v === '' || v == null) return resolvedEmptyLabel
    const hit = options.find((o) => o.value === v || String(o.value) === String(v))
    return hit?.label ?? String(v)
  }

  const muiRenderValue = (
    multiple
      ? renderValue
        ? (selected: T | T[]) => renderValue(selected as T[])
        : (selected: T | T[]) => {
            const arr = selected as T[]
            if (!arr.length) return resolvedEmptyLabel
            return arr.map((v) => resolveOptionLabel(v)).join(', ')
          }
      : renderValue
        ? (selected: T | T[]) => (renderValue as SelectProps<T>['renderValue'])?.(selected as T)
        : (selected: T | T[]) => resolveOptionLabel(selected as T)
  ) as SelectProps<T | T[]>['renderValue'] | undefined

  const outlineLabels = isOutlineLabelLayout(surface) && !labelAsPlaceholder

  /** Đã chọn + cho phép trống: chỉ hiện X xóa, không mũi tên dropdown. */
  const hasSelectedValue = multiple
    ? Array.isArray(value) && (value as T[]).length > 0
    : value !== '' && value != null
  const resolvedIconComponent =
    IconComponent ??
    (allowEmpty && hasSelectedValue && !disabled
      ? ({ className }: { className?: string }) => (
          <CloseOutlinedIcon
            className={className}
            sx={{ cursor: 'pointer', pointerEvents: 'auto' }}
            onMouseDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (multiple) {
                const clearMulti = onChange as (v: T[]) => void
                clearMulti([])
              } else {
                const clearSingle = onChange as (v: T | '') => void
                clearSingle('')
              }
            }}
          />
        )
      : undefined)

  const selectNode = (
    <Select<T | T[]>
      multiple={multiple}
      labelId={outlineLabels ? labelId : undefined}
      id={id}
      value={value as T | T[]}
      label={outlineLabels ? label : undefined}
      onChange={handleChange}
      disabled={disabled}
      displayEmpty={!multiple && (displayEmpty ?? allowEmpty ?? labelAsPlaceholder)}
      renderValue={muiRenderValue}
      IconComponent={resolvedIconComponent}
      notched={outlineLabels ? (outlineShrink ? true : undefined) : false}
      MenuProps={{
        ...MenuProps,
        disablePortal,
      }}
    >
      {allowEmpty || value === '' ? (
        <MenuItem value="" sx={allowEmpty ? undefined : { display: 'none' }}>
          <em>{resolvedEmptyLabel}</em>
        </MenuItem>
      ) : null}
      {options.map((opt) => (
        <MenuItem key={String(opt.value)} value={opt.value} disabled={opt.disabled}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  )

  if (isAboveLabelLayout(surface) && !labelAsPlaceholder) {
    return (
      <FormFieldShell
        label={shellLabel}
        labelAction={labelAction}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        fullWidth={resolvedFullWidth}
        size={resolvedSize}
        id={id}
        surface={surface}
        sx={sx}
      >
        <FormControl
          className={KIOT_FORM_FIELD_CLASS}
          fullWidth
          size={resolvedSize}
          disabled={disabled}
          error={error}
          required={required}
          variant="outlined"
          sx={getFormFieldRootSx(surface)}
        >
          {selectNode}
        </FormControl>
      </FormFieldShell>
    )
  }

  if (labelAsPlaceholder) {
    return (
      <FormControl
        className={KIOT_FORM_FIELD_CLASS}
        fullWidth={resolvedFullWidth}
        size={resolvedSize}
        disabled={disabled}
        error={error}
        required={required}
        variant="outlined"
        sx={mergeSx(getFormFieldRootSx(surface), sx)}
      >
        {selectNode}
        {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      </FormControl>
    )
  }

  return (
    <FormControl
      fullWidth={resolvedFullWidth}
      size={resolvedSize}
      disabled={disabled}
      error={error}
      required={required}
      variant="outlined"
      sx={mergeSx(getFormFieldRootSx(surface), getFormFieldOutlineLabelSx(surface), sx)}
    >
      {label ? (
        <InputLabel id={labelId} shrink={outlineShrink ? true : undefined}>
          {label}
        </InputLabel>
      ) : null}
      {selectNode}
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  )
}

export default SelectInput
